import React, { useState } from 'react'
import InvestorDetailModal from './InvestorDetailModal'
import css from './style.scss'

const RANK_COLORS = ['#7C3AED', '#4F46E5', '#2563EB']
const OTHER_COLOR = '#64748B'

function fmt(n) {
  if (n == null || n === 0) return '—'
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `$${Math.round(n / 1000).toLocaleString()}K`
  return `$${n.toLocaleString()}`
}

function classifyStrategy(inv) {
  const acq12 = inv.acquisitions?.totalAcquisitionCountLast12Months || 0
  const disp12 = inv.dispositions?.totalDispositionCountLast12Months || 0
  const avgOwnership = inv.currentHoldings?.avgOwnershipLengthMonths || 0
  const holdings = inv.currentHoldings?.totalPropertyCount || 0
  if (avgOwnership > 0 && avgOwnership < 18 && disp12 > 0) return 'Fix & Flip'
  if (avgOwnership >= 36) return 'Buy & Hold'
  if (holdings > 5 && acq12 >= 2) return 'Portfolio Builder'
  return 'Investor'
}

function getClassifications(inv) {
  if (inv.classification && inv.classification.length > 0) return inv.classification
  return [classifyStrategy(inv)]
}

export function computeMatchScore(inv) {
  const acq12 = inv.acquisitions?.totalAcquisitionCountLast12Months || 0
  const holdCount = inv.currentHoldings?.totalPropertyCount || 0
  const cashCount = inv.currentHoldings?.cashPurchasePropertyCount || 0
  const financedCount = inv.currentHoldings?.financedPropertyCount || 0
  const total = cashCount + financedCount
  const cashPct = total > 0 ? (cashCount / total) * 100 : 0
  const strategy = getClassifications(inv)[0]
  return Math.min(100, Math.round(
    Math.min(acq12 * 5, 30) +
    Math.min(holdCount * 2, 20) +
    (cashPct / 100) * 20 +
    15 +
    (strategy !== 'Investor' ? 15 : 5)
  ))
}

function buildMatchTags(inv) {
  const tags = []
  const acq12 = inv.acquisitions?.totalAcquisitionCountLast12Months || 0
  const holdCount = inv.currentHoldings?.totalPropertyCount || 0
  const cashCount = inv.currentHoldings?.cashPurchasePropertyCount || 0
  const financedCount = inv.currentHoldings?.financedPropertyCount || 0
  const total = cashCount + financedCount
  const cashPct = total > 0 ? Math.round((cashCount / total) * 100) : 0
  const singleFamily = inv.currentHoldings?.propertyTypeDistribution?.residentialSingleFamilyCount || 0

  if (acq12 >= 3) tags.push('Very Active (3+ purchases/year)')
  else if (acq12 >= 1) tags.push('Active Buyer')
  if (holdCount >= 10) tags.push('Large Portfolio')
  if (cashPct >= 50) tags.push('Cash Buyer')
  if (singleFamily > 0) tags.push('SFH Specialist')
  return tags
}

function buildAnalysis(inv) {
  const strategy = getClassifications(inv)[0]
  const acq12 = inv.acquisitions?.totalAcquisitionCountLast12Months || 0
  const holdCount = inv.currentHoldings?.totalPropertyCount || 0
  const avgPrice = inv.currentHoldings?.avgPurchasePrice || 0
  let text = `${inv.type || 'Investor'} with ${holdCount} properties.`
  if (acq12 > 0) text += ` Purchased ${acq12} properties in the last 12 months.`
  if (avgPrice > 0) text += ` Average purchase price: ${fmt(avgPrice)}.`
  text += ` Investment strategy: ${strategy}.`
  return text
}

export default function InvestorCard({ investor, rank, selected, checked, dimmed, onSelect, onToggleChecked }) {
  const [showModal, setShowModal] = useState(false)

  const acq = investor.acquisitions || {}
  const hold = investor.currentHoldings || {}
  const contacts = investor.contacts || []
  const classifications = getClassifications(investor)
  const matchScore = computeMatchScore(investor)
  const matchTags = buildMatchTags(investor)
  const analysis = buildAnalysis(investor)

  const color = rank < RANK_COLORS.length ? RANK_COLORS[rank] : OTHER_COLOR
  const scoreColor = matchScore >= 80 ? '#16a34a' : matchScore >= 50 ? '#2563EB' : '#64748B'

  const acq12 = acq.totalAcquisitionCountLast12Months || 0
  const holdCount = hold.totalPropertyCount || 0
  const avgPrice = hold.avgPurchasePrice || 0
  const avgOwnership = hold.avgOwnershipLengthMonths || 0

  // Extract phone and email from nested contact structure
  const firstContact = contacts[0]
  const firstPhone = firstContact?.phones?.find((p) => p.reachable)?.number || firstContact?.phones?.[0]?.number
  const firstEmail = firstContact?.emails?.[0]?.email

  const cardClasses = [
    css.investorCard,
    selected && css.investorCardSelected,
    checked && css.investorCardChecked,
    dimmed && css.investorCardDimmed,
  ].filter(Boolean).join(' ')

  return (
    <div
      className={cardClasses}
      onClick={() => onSelect(investor.id)}
    >
      {/* Checkbox to filter map */}
      {onToggleChecked && (
        <label
          className={css.investorCheckboxLabel}
          onClick={(e) => e.stopPropagation()}
          title="Show only this investor on map"
        >
          <input
            type="checkbox"
            className={css.investorCheckbox}
            checked={!!checked}
            onChange={() => onToggleChecked(investor.id)}
          />
        </label>
      )}

      {/* Rank badge */}
      <div className={css.rankBadge} style={{ background: color }}>
        {rank === 0 ? '#1 BEST MATCH' : `#${rank + 1}`}
      </div>

      {/* Name and score */}
      <div className={css.cardNameRow}>
        <div className={css.investorName}>{investor.name || 'Unknown Investor'}</div>
        <div className={css.matchScoreInline}>
          <div className={css.matchBarSmall}>
            <div className={css.matchBarFill} style={{ width: `${matchScore}%`, background: scoreColor }} />
          </div>
          <span style={{ color: scoreColor }}>{matchScore}%</span>
        </div>
      </div>

      {/* Strategy tag */}
      <div className={css.tagRow}>
        {classifications.map((tag, i) => (
          <span key={i} className={css.strategyTag}>{tag}</span>
        ))}
      </div>

      {/* Stats grid */}
      <div className={css.statsGrid}>
        <div className={css.statItem}>
          <div className={css.statLabel}>Portfolio Size</div>
          <div className={css.statValue}>{holdCount} Properties</div>
        </div>
        <div className={css.statItem}>
          <div className={css.statLabel}>Last 12 Months</div>
          <div className={css.statValue}>{acq12} Purchases</div>
        </div>
        <div className={css.statItem}>
          <div className={css.statLabel}>Avg Purchase Price</div>
          <div className={css.statValue}>{fmt(avgPrice)}</div>
        </div>
        <div className={css.statItem}>
          <div className={css.statLabel}>Ownership</div>
          <div className={css.statValue}>{avgOwnership > 0 ? `${avgOwnership} months avg` : '—'}</div>
        </div>
      </div>

      {/* Why This Match section */}
      <div className={css.whyMatch}>
        <div className={css.whyMatchTitle}>WHY THIS MATCH?</div>
        <div className={css.whyMatchText}>{analysis}</div>
        <div className={css.matchTagsRow}>
          {matchTags.map((tag, i) => (
            <span key={i} className={css.matchTag}>{tag}</span>
          ))}
        </div>
      </div>

      {/* Contact info */}
      {(firstPhone || firstEmail) && (
        <div className={css.contactSection}>
          {firstPhone && (
            <div className={css.contactRow}>
              <span className={css.contactIcon}>P</span>
              <span>{firstPhone}</span>
            </div>
          )}
          {firstEmail && (
            <div className={css.contactRow}>
              <span className={css.contactIcon}>@</span>
              <span>{firstEmail}</span>
            </div>
          )}
        </div>
      )}

      {/* View Details button - opens modal */}
      <button
        className={css.expandButton}
        onClick={(e) => { e.stopPropagation(); setShowModal(true) }}
      >
        View Full Details
        <span className={css.expandArrow}>{'›'}</span>
      </button>

      {/* Detail modal */}
      {showModal && (
        <InvestorDetailModal
          investor={investor}
          rank={rank}
          matchScore={matchScore}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  )
}
